import { useAudioStore } from '@/stores/audioStore';
import { STRINGS } from '@/strings/strings';
import { cn } from '@/lib/utils';
import { formatTime } from './helpers';

/**
 * Scrubbable progress bar + current/total timestamp row. Subscribes to
 * `audioStore` directly — callers don't pass props beyond layout-only
 * className (to apply per-surface max-width constraints: Inline caps at
 * 468 px tablet / 300 px desktop; Now-Playing uses different caps).
 *
 * Visual: dream-gold range-accent on dark gradient bg; timestamps are
 * white-with-opacity-50 (assumes gradient parent — both Inline + Now-
 * Playing provide that).
 */
interface AudioProgressBarProps {
  className?: string;
}

export function AudioProgressBar({ className }: AudioProgressBarProps) {
  const currentTimeS = useAudioStore((s) => s.currentTimeS);
  const totalDurationS = useAudioStore((s) => s.totalDurationS);
  const seek = useAudioStore((s) => s.seek);

  const copy = STRINGS.storyOutput.page.audio;

  return (
    <div className={cn('flex w-full flex-col items-start gap-2.5', className)}>
      <input
        type="range"
        min={0}
        max={Math.max(totalDurationS, 1)}
        step={1}
        value={currentTimeS}
        onChange={(e) => seek(Number(e.currentTarget.value))}
        aria-label={copy.ariaLabel.scrub}
        className="w-full accent-dream-gold"
      />
      <div className="flex w-full items-start justify-between text-sm leading-none text-dream-white md:text-base">
        <span className="tabular-nums opacity-50">{formatTime(currentTimeS)}</span>
        <span className="tabular-nums opacity-50">{formatTime(totalDurationS)}</span>
      </div>
    </div>
  );
}
